import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Users, UserPlus, Eye, MapPin, Calendar } from "lucide-react";
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { Box, Paper, Typography } from '@mui/material';

type AnalyticsData = {
  totalVisitors: number;
  todayVisitors: number;
  newVisitors: number;
  returningVisitors: number;
  topCountries: Array<{ country: string; count: number }>;
  dailyVisits: Array<{ date: string; count: number }>;
};

const AnalyticsCard = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      
      // Visiteurs total
      const { data: totalData, error: totalError } = await supabase
        .from('visitor_analytics')
        .select('id', { count: 'exact' });

      if (totalError) throw totalError;

      // Visiteurs aujourd'hui
      const today = new Date().toISOString().split('T')[0];
      const { data: todayData, error: todayError } = await supabase
        .from('visitor_analytics')
        .select('id', { count: 'exact' })
        .gte('visited_at', today);

      if (todayError) throw todayError;

      // Nouveaux vs connus
      const { data: newVisitorsData, error: newError } = await supabase
        .from('visitor_analytics')
        .select('id', { count: 'exact' })
        .eq('is_returning_visitor', false);

      const { data: returningData, error: returningError } = await supabase
        .from('visitor_analytics')
        .select('id', { count: 'exact' })
        .eq('is_returning_visitor', true);

      if (newError || returningError) throw newError || returningError;

      // Top pays
      const { data: countriesData, error: countriesError } = await supabase
        .from('visitor_analytics')
        .select('country')
        .not('country', 'is', null);

      if (countriesError) throw countriesError;

      // Traiter les données des pays
      const countryMap = new Map<string, number>();
      countriesData?.forEach(item => {
        if (item.country) {
          countryMap.set(item.country, (countryMap.get(item.country) || 0) + 1);
        }
      });

      const topCountries = Array.from(countryMap.entries())
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Visites des 7 derniers jours
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { data: weeklyData, error: weeklyError } = await supabase
        .from('visitor_analytics')
        .select('visited_at')
        .gte('visited_at', sevenDaysAgo.toISOString());

      if (weeklyError) throw weeklyError;

      // Traiter les données quotidiennes
      const dailyMap = new Map<string, number>();
      weeklyData?.forEach(item => {
        const date = new Date(item.visited_at).toISOString().split('T')[0];
        dailyMap.set(date, (dailyMap.get(date) || 0) + 1);
      });

      const dailyVisits = Array.from(dailyMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

      setAnalytics({
        totalVisitors: totalData?.length || 0,
        todayVisitors: todayData?.length || 0,
        newVisitors: newVisitorsData?.length || 0,
        returningVisitors: returningData?.length || 0,
        topCountries,
        dailyVisits
      });

    } catch (error) {
      console.error('Erreur lors du chargement des analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Paper className="p-4 border-gray-600" style={{ backgroundColor: '#1f1f1f' }}>
        <Box className="text-center py-4">
          <Typography variant="h6" className="text-white mb-2">
            <Eye className="h-5 w-5 inline mr-2" />
            Statistiques du site
          </Typography>
          <Typography variant="body1" className="text-gray-300">Chargement des statistiques...</Typography>
        </Box>
      </Paper>
    );
  }

  if (!analytics) {
    return (
      <Paper className="p-4 border-gray-600" style={{ backgroundColor: '#1f1f1f' }}>
        <Box className="text-center py-4">
          <Typography variant="h6" className="text-white mb-2">
            <Eye className="h-5 w-5 inline mr-2" />
            Statistiques du site
          </Typography>
          <Typography variant="body1" className="text-gray-300">
            Aucune donnée disponible
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <div className="space-y-8">
      {/* Statistiques principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        <Paper 
          className="p-6 text-center border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1" 
          style={{ 
            backgroundColor: '#1a1a1a', 
            color: 'white',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
            borderRadius: '16px'
          }}
        >
          <Box className="flex items-center justify-center gap-2 mb-3">
            <div className="p-2 rounded-full" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
              <Users className="h-5 w-5 text-white" />
            </div>
            <Typography variant="body2" className="font-semibold text-gray-300">Total Visiteurs</Typography>
          </Box>
          <Typography variant="h3" className="font-black text-white mb-1">{analytics.totalVisitors}</Typography>
          <div className="h-1 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 opacity-70"></div>
        </Paper>
        
        <Paper 
          className="p-6 text-center border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1" 
          style={{ 
            backgroundColor: '#1a1a1a', 
            color: 'white',
            background: 'linear-gradient(135deg, #065f46 0%, #059669 100%)',
            borderRadius: '16px'
          }}
        >
          <Box className="flex items-center justify-center gap-2 mb-3">
            <div className="p-2 rounded-full" style={{ background: 'linear-gradient(135deg, #10b981, #047857)' }}>
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <Typography variant="body2" className="font-semibold text-gray-100">Aujourd'hui</Typography>
          </Box>
          <Typography variant="h3" className="font-black text-white mb-1">{analytics.todayVisitors}</Typography>
          <div className="h-1 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 opacity-80"></div>
        </Paper>
        
        <Paper 
          className="p-6 text-center border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1" 
          style={{ 
            backgroundColor: '#1a1a1a', 
            color: 'white',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            borderRadius: '16px'
          }}
        >
          <Box className="flex items-center justify-center gap-2 mb-3">
            <div className="p-2 rounded-full" style={{ background: 'linear-gradient(135deg, #60a5fa, #2563eb)' }}>
              <UserPlus className="h-5 w-5 text-white" />
            </div>
            <Typography variant="body2" className="font-semibold text-gray-100">Nouveaux</Typography>
          </Box>
          <Typography variant="h3" className="font-black text-white mb-1">{analytics.newVisitors}</Typography>
          <div className="h-1 rounded-full bg-gradient-to-r from-blue-400 to-blue-500 opacity-80"></div>
        </Paper>
        
        <Paper 
          className="p-6 text-center border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1" 
          style={{ 
            backgroundColor: '#1a1a1a', 
            color: 'white',
            background: 'linear-gradient(135deg, #581c87 0%, #8b5cf6 100%)',
            borderRadius: '16px'
          }}
        >
          <Box className="flex items-center justify-center gap-2 mb-3">
            <div className="p-2 rounded-full" style={{ background: 'linear-gradient(135deg, #a78bfa, #7c3aed)' }}>
              <Users className="h-5 w-5 text-white" />
            </div>
            <Typography variant="body2" className="font-semibold text-gray-100">Fidèles</Typography>
          </Box>
          <Typography variant="h3" className="font-black text-white mb-1">{analytics.returningVisitors}</Typography>
          <div className="h-1 rounded-full bg-gradient-to-r from-purple-400 to-purple-500 opacity-80"></div>
        </Paper>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Graphique camembert nouveaux vs connus visiteurs */}
        <Paper 
          className="p-6 border-0 shadow-xl hover:shadow-2xl transition-all duration-300" 
          style={{ 
            backgroundColor: '#1a1a1a',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
            borderRadius: '20px'
          }}
        >
          <div className="mb-6">
            <Typography variant="h5" className="font-black text-white mb-2">
              Répartition des visiteurs
            </Typography>
            <Typography variant="body2" className="text-gray-400">
              Distribution nouveaux vs fidèles
            </Typography>
          </div>
          <div className="flex justify-center">
            <PieChart
              series={[
                {
                  data: [
                    { 
                      id: 0, 
                      value: analytics.newVisitors, 
                      label: 'Nouveaux', 
                      color: '#3b82f6'
                    },
                    { 
                      id: 1, 
                      value: analytics.returningVisitors, 
                      label: 'Fidèles', 
                      color: '#8b5cf6'
                    },
                  ],
                  highlightScope: { fade: 'global', highlight: 'item' },
                },
              ]}
              width={420}
              height={240}
              sx={{
                '& .MuiChartsLegend-series text': {
                  fill: 'white !important',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiChartsTooltip-root': {
                  backgroundColor: '#1a1a1a',
                  color: 'white',
                  borderRadius: '12px',
                  border: '1px solid #374151',
                },
                '& .MuiChartsLegend-mark': {
                  borderRadius: '4px',
                },
                '& .MuiChartsLegend-series': {
                  '& text': {
                    fill: 'white !important',
                  },
                },
              }}
            />
          </div>
        </Paper>

        {/* Graphique en barres des pays */}
        <Paper 
          className="p-6 border-0 shadow-xl hover:shadow-2xl transition-all duration-300" 
          style={{ 
            backgroundColor: '#1a1a1a',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
            borderRadius: '20px'
          }}
        >
          <div className="mb-6">
            <Typography variant="h5" className="font-black text-white mb-2">
              Top Pays
            </Typography>
            <Typography variant="body2" className="text-gray-400">
              Origine géographique des visiteurs
            </Typography>
          </div>
          {analytics.topCountries.length > 0 ? (
            <div className="flex justify-center">
              <BarChart
                xAxis={[{ 
                  scaleType: 'band', 
                  data: analytics.topCountries.map(c => c.country),
                  tickLabelStyle: { 
                    fill: 'white',
                    fontSize: 12,
                    fontWeight: 500,
                  }
                }]}
                series={[{ 
                  data: analytics.topCountries.map(c => c.count),
                  color: '#10b981',
                }]}
                width={420}
                height={240}
                sx={{
                  '& .MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
                    fill: 'white',
                    fontSize: '12px',
                  },
                  '& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                    fill: 'white',
                    fontSize: '12px',
                  },
                  '& .MuiChartsAxis-line': {
                    stroke: '#374151',
                  },
                  '& .MuiChartsAxis-tick': {
                    stroke: '#374151',
                  },
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-60 bg-gray-800/30 rounded-xl">
              <Typography className="text-gray-400 text-center">
                Aucune donnée de pays disponible
              </Typography>
            </div>
          )}
        </Paper>
      </div>

      {/* Graphique en ligne des visites quotidiennes */}
      <Paper 
        className="p-8 border-0 shadow-xl hover:shadow-2xl transition-all duration-300" 
        style={{ 
          backgroundColor: '#1a1a1a',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          borderRadius: '24px'
        }}
      >
        <div className="mb-8">
          <Typography variant="h4" className="font-black text-white mb-3">
            Évolution des visites
          </Typography>
          <Typography variant="h6" className="text-gray-400 mb-1">
            Tendance sur les 7 derniers jours
          </Typography>
          <div className="h-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 w-32"></div>
        </div>
        {analytics.dailyVisits.length > 0 ? (
          <div className="flex justify-center overflow-x-auto">
            <LineChart
              xAxis={[{ 
                scaleType: 'point', 
                data: analytics.dailyVisits.map(d => new Date(d.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })),
                tickLabelStyle: { 
                  fill: 'white',
                  fontSize: 13,
                  fontWeight: 500,
                }
              }]}
              series={[
                {
                  data: analytics.dailyVisits.map(d => d.count),
                  area: true,
                  color: '#f59e0b',
                  curve: 'catmullRom',
                },
              ]}
              width={Math.max(700, analytics.dailyVisits.length * 120)}
              height={350}
              sx={{
                '& .MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
                  fill: 'white',
                  fontSize: '13px',
                },
                '& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                  fill: 'white',
                  fontSize: '13px',
                },
                '& .MuiChartsAxis-line': {
                  stroke: '#374151',
                  strokeWidth: 2,
                },
                '& .MuiChartsAxis-tick': {
                  stroke: '#374151',
                },
                '& .MuiAreaElement-series-auto-generated-id-0': {
                  fill: 'url(#areaGradient)',
                },
              }}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-80 bg-gray-800/30 rounded-xl">
            <div className="text-center">
              <Typography variant="h5" className="text-gray-400 mb-2">Graphique</Typography>
              <Typography className="text-gray-400">
                Aucune donnée de visite disponible
              </Typography>
            </div>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default AnalyticsCard;